/** zh-CN catalogue `cmdMotion` (W3-09; English: src/core/commands/motion.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

const VALUE_TYPE =
  '{type, select, number {数字} rgb {颜色} string {文字} vec2 {一对数字} rgba8 {带不透明度的颜色} other {对应类型的值}}';

const catalog: MessageCatalog = {
  ns: 'cmdMotion',
  locale: 'zh-CN',
  messages: {
    trackMissing: '此运动已不存在。它可能已被另一项更改移除。',
    keyMissing: '选中的关键帧已不存在。',
    lineMissing: '此判定线已不存在。',
    noKeys: '此运动没有关键帧。',
    noTrack: '此判定线上的这个属性没有动画。',
    nothingAnimated: '此判定线上还没有任何动画。请先为某个属性添加关键帧。',
    keysCollide: '两个关键帧会落在同一拍上。请在它们之间至少留一个网格步长。',
    beforeZero: '关键帧和音符不能移到第 0 拍之前。',
    valueType: `此值不适用于该属性（需要${VALUE_TYPE}）。`,
    valueNotFinite: '请输入一个有限的数字。',
    layerOnExtended: '只有位置、旋转、不透明度和流速有层级。',
    layerInvalid: '层级从 0 开始编号。',
    layerLimit: '一个属性最多可以有 {max} 个层级。',
    notLayered: '只有位置、旋转、不透明度和流速有层级。',
    gifNeedsGifTexture: 'GIF 进度需要此判定线使用 GIF 图片。',
    varMissing: '此特效或视频数值已不存在。',
    firstKeyDelete: '还有其他关键帧时，不能删除第一个关键帧。请使用“重置属性”移除整个运动。',
    firstKeyJump: '第一个关键帧不能跳变：没有任何运动到达它。',
    gifTrailingJump: 'GIF 的最后一个关键帧不能跳变：否则 GIF 会停止播放。',
    arriveAfterHold: '钩定区段之后，到达值就是保持的值。请改为修改钩定关键帧，或关闭钩定。',
    jumpKeptAfterHold: '保留了 {count, plural, other {# 个跳变}}：钩定区段之后，到达值就是保持的值。',
    noSegments: '请选中一个或多个区段（两个关键帧之间的曲线）。',
    easeSkipped: '有 {count, plural, other {# 个区段}}未作更改，因为那里不允许使用此缓动。',
    splitOutside: '拆分需要一个位于区段内、两个关键帧之间的拍数。',
    splitApprox: '已近似拆分曲线（偏差在其范围的 {percent}% 以内）。烘焙可获得精确结果。',
    splitApproxText:
      '{ms, plural, =0 {已拆分文字：Phira 现在会在每一半中分别计算显示的字符。在这里每个字符仍在相同的时刻出现。} other {已近似拆分文字：Phira 在每一半中计算显示字符的方式不同，因此部分字符会提前或推迟最多 {ms} 毫秒出现。需要精确时间时，请保持逐字显示区段完整。}}',
    splitApproxColor: '已近似拆分颜色：新关键帧已取整为整数颜色值。',
    splitBaked:
      '这条曲线的一部分在 Phira 中无法精确拆分，因此已将其转换为贴合它的短直线分段（偏差在其范围的 {percent}% 以内）。',
    splitBakedText:
      '{ms, plural, =0 {这条文字曲线的一部分在 Phira 中无法精确拆分，因此已将其转换为短分段。每个字符仍在相同的时刻出现。} other {这条文字曲线的一部分在 Phira 中无法精确拆分，因此已将其转换为短分段。字符可能会提前或推迟最多 {ms} 毫秒出现。}}',
    splitLeadIn:
      '在第一个关键帧之前，此运动会向前延续其第一条曲线。新关键帧在那里开始了一个直线区段，使开头最多改变其范围的 {percent}%。如需平稳的开头，请使用“保持第一个值”。',
    splitApproxGif: 'GIF 在这里原本以自然速度自行播放。现在这个关键帧控制它的这一部分。',
    splitLinearHalf: '此关键帧旁边的曲线无法在新数值下保持形状，因此那部分变成了直线。',
    splitPartner:
      'X 和 Y 位置已关联，因此另一个轴也在此拍得到了一个关键帧。{kind, select, bake {其曲线的一部分已转换为短直线分段} leadIn {其在第一个关键帧之前的开头已改变} other {其曲线已被近似拆分}}（偏差在其范围的 {percent}% 以内）。',
    bakeDivInvalid: '步长必须是一拍的整分数（每拍 1 到 30720 段）。',
    bakeTooMany: '烘焙会产生超过 {max} 个分段。请选择更粗的步长或更少的区段。',
    joinNotExact: '这两个区段无法合并为一条精确的曲线。（拟合功能计划在以后的版本中提供。）',
    joinNeedsMiddle: '合并需要两个区段之间的一个关键帧。',
    joinPartner: 'X 和 Y 位置已关联，而另一个轴无法在此拍精确合并。',
    extrapolateNeedsTwo: '递推需要选中区段之前的两个区段。',
    extrapolateSkipped: '已跳过 {count, plural, other {# 个区段}}：递推需要它之前的两个数值区段。',
    linkNeedsTwo: '请至少选中两个区段进行绑定。',
    linkPrimary: '要复制的区段必须是选中的区段之一。',
    linkValueType: '只有数值类型相同的区段才能绑定（数字、颜色或文字）。',
    exprNoNumbers: '表达式只适用于数值关键帧。',
    exprSkipped: '有 {count, plural, other {# 个关键帧}}未作更改：表达式在那里没有得到有效的数字。',
    leadInAlready: '此运动已经从第 0 拍开始。',
    replaceEmpty: '不能写入空的事件列表。请使用“重置属性”移除运动。',
    replaceOrder: '事件必须按时间顺序排列且不能重叠。',
    replaceValue: `有一个事件的值不适用于此属性（需要${VALUE_TYPE}）。`,
    replaceEase: '有一个事件使用了此属性不能使用的缓动。',
    moveNotLayered: '只有位置、旋转、不透明度和流速的区段可以移到另一个层级。',
    nothingToMove: '选中的区段已经在那个层级上。',
    noFreeLayer: '此属性已没有空闲的层级（最多 4 个）。',
    negateNothing: '取反适用于位置、旋转和流速关键帧；对不透明度则设为 0%。',
    negateSkipped: '有 {count, plural, other {# 个关键帧}}未作更改：只有位置、旋转、速度和不透明度可以取反。',
    opacityNothing: '“完全不透明”只适用于不透明度关键帧。',
    opacitySkipped: '有 {count, plural, other {# 个关键帧}}未作更改：它们不是不透明度。',
    timeScaleInvalid: '时间缩放必须是两个正整数的分数，例如 2/1 或 1/2。',
    transformCollide: '关键帧会越过未选中的关键帧。请把它们也选中，或使用更小的变化。',
    transformNothing: '请选中要变换的音符或关键帧。',
    timeLengthInvalid: '要插入的长度必须大于 0 拍。',
    timeRangeInvalid: '请选择一个从第 0 拍或之后开始、且结束晚于开始的范围。',
    timeLeadIn:
      '在第一个关键帧之前，此运动会向前延续其第一条曲线。那条曲线在时间上移动了，使开头最多改变其范围的 {percent}%。如需平稳的开头，请使用“保持第一个值”。',
    timeShrunk:
      '有 {count, plural, other {# 条曲线}}在被移除的范围内没有关键帧，因此被整体缩短，与“插入时间”相反。如果想把这段范围剪掉，请先在两端添加关键帧。',
    reverseNothing: '请选中要反转的音符或区段。',
    pasteNothing: '此处没有可粘贴的内容。',
    pasteRepeatInvalid: '重复需要整数份副本，且间距大于 0 拍。',
    pasteUnmatched: '有 {count, plural, other {# 个值}}在同一拍没有可叠加的关键帧。请将其余部分作为新层级粘贴。',
    pasteSkipped:
      '有 {count, plural, other {# 项}}未粘贴：判定线不存在、该属性不适用于此判定线、那里不允许使用其缓动，或会落在第 0 拍之前。',
    pasteNotesOnUiMover: '有 {count, plural, other {# 个音符}}未粘贴：UI 绑定线上的音符将不可见。',
    pasteHitsoundsDropped: '有 {count, plural, other {# 个自定义打击音}}不在此项目中；那些音符使用默认音效。',
    pasteLinesNothing: '剪贴板中没有判定线。请先从判定线面板复制判定线。',
    pasteLinesMissingAssets:
      '此项目中缺少 {count, plural, other {# 个文件}}。使用这些文件的判定线和音符会显示默认外观。',
  },
};

export default catalog;
